import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import * as PropTypes from 'prop-types';
import {
  Modal, Form, Select, Input, Radio, Button, Popover, Row, Typography, Tag,
} from 'antd';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
  selectEditState, selectDamageTypesRequest, selectDamageTypes, selectModelTypes,
  selectModelTypesRequest,
  selectSubmitDamageAreaRequest,
  selectFetchDamageTypesForAreaRequest,
} from '../../../../state/ui/configuration/inspection/damageAreaUiReducer';
import {
  cancelEditDamageArea, requestAddDamageArea, requestUpdateDamageArea, requestDamageTypesForArea,
} from '../../../../state/ui/configuration/inspection/damageAreaUiActions';
import { selectDamageArea } from '../../../../state/domain/damageAreas/damageAreasReducer';
import {
  damageAreaLocations, filterSelectField, damageTypeShape, modelTypeShape, damageAreaShape,
} from './config-util';
import ViaValidationAlert from '../../../../components/fields/ViaValidationAlert';

const { Text } = Typography;

/**
 * The field for the description label. Allows clear, and max length set. Required plus extra duplicate validation
 * The rules are injected into the form using the form field decorator.
 */
const DescriptionField = injectIntl(({
  form, area, intl, checkForDuplicates,
}) => (
  <Form.Item required label={<FormattedMessage id="description" />}>
    { form.getFieldDecorator('damageAreaDescription', {
      initialValue: area ? area.damageAreaDescription : undefined,
      rules: [
        { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
        {
          validator(rule, value, callback) {
            if (value !== (area ? area.damageAreaDescription : '')
              && checkForDuplicates(area ? area.damageAreaCode : null, value)) {
              callback(intl.formatMessage({ id: 'Duplicate' }));
            }
            callback();
          },
        },
      ],
    })(
      <Input
        type="text"
        maxLength={80}
        allowClear
        placeholder={intl.formatMessage({ id: 'enter' })}
      />,
    )}
  </Form.Item>
));

/**
 * The field for the damage area location.
 * The rules are injected into the form using the form field decorator.
 */
const LocationField = injectIntl(({ form, area, intl }) => (
  <Form.Item visible={false} required label={<FormattedMessage id="location" />}>
    { form.getFieldDecorator('damageAreaLocation', {
      initialValue: area ? area.damageAreaLocation : 'INTERIOR',
      rules: [
        { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
        { type: 'enum', enum: Object.values(damageAreaLocations) },
      ],
    })(
      <Radio.Group buttonStyle="solid">
        <Radio.Button value={damageAreaLocations.INTERIOR}><FormattedMessage id="interior" /></Radio.Button>
        <Radio.Button value={damageAreaLocations.EXTERIOR}><FormattedMessage id="exterior" /></Radio.Button>
      </Radio.Group>,
    )}
  </Form.Item>
));

/**
 * The field for the model types linked to the damage area.
 * The rules are injected into the form using the form field decorator.
 */
const ModelTypeField = injectIntl(({
  form, area, modelTypes, intl, loading = false,
}) => (
  <Form.Item required label={<FormattedMessage id="modelType" />}>
    { form.getFieldDecorator('linkedModelTypeCodes', {
      initialValue: (area && area.linkedModelTypeCodes && modelTypes
        ? area.linkedModelTypeCodes.map(code => modelTypes.find(mt => `${mt.modelTypeCode}` === `${code}`)).map(mt => mt.modelTypeCode)
        : []
      ),
      rules: [
        { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
      ],
    })(
      <Select
        mode="multiple"
        showArrow
        placeholder={<FormattedMessage id="selectMulti" />}
        loading={loading}
        filterOption={filterSelectField}
      >
        {
      modelTypes.map(mt => (
        <Select.Option key={mt.modelTypeCode} value={mt.modelTypeCode}>{mt.modelTypeDescription}</Select.Option>
      ))
    }
      </Select>,
    )}
  </Form.Item>
));

const damageTypeFieldId = 'linkedDamageTypeCodes';

/**
 * The field for damage types linked to the damage area.
 * The rules are injected into the form using the form field decorator.
 */
const createDamageTypeField = (form, area, damageTypes, intl, loading) => {
  // Define the data / rules for the field.
  const decorator = form.getFieldDecorator(damageTypeFieldId, {
    initialValue: (area && area.linkedDamageTypeCodes && damageTypes
      ? area.linkedDamageTypeCodes.map(code => damageTypes.find(dt => `${dt.damageTypeCode}` === `${code}`)).map(dt => dt.damageTypeDescription)
      : []
    ),
    rules: [
      { required: true, message: intl.formatMessage({ id: 'validationEmpty' }) },
    ],
  });

  // Create the field and decorate with defined data / rules from above.
  return decorator(
    <Select
      mode="multiple"
      showArrow
      placeholder={<FormattedMessage id="selectMulti" />}
      loading={loading}
      optionLabelProp="label"
      optionFilterProp="label"
      filterOption={
        (input, option) => (
          option.props.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
        )}
    >
      {damageTypes.map(dt => (
        <Select.Option key={dt.damageTypeCode} value={dt.damageTypeDescription} label={dt.damageTypeDescription}>
          <Row type="flex" justify="space-between" align="middle">
            <Text style={{ width: '60%', whiteSpace: 'pre-wrap' }}>{dt.damageTypeDescription}</Text>
            <Tag color="volcano" style={{ display: dt.active ? 'none' : '', height: '22px' }}>
              <FormattedMessage id="notPublished" />
            </Tag>
          </Row>
        </Select.Option>
      ))}
    </Select>,
  );
};


/**
 * The popover for adding a new damage type in the damage pop
 */
const CreateDamagePopover = injectIntl(({
  intl, damageTypes, onCreate, children, ...rest
}) => {
  const [damageDesc, setDamageDesc] = useState('');

  const [validationStatus, setValidationStatus] = useState({ validateStatus: null, help: '', hasFeedback: false });

  const validate = (description) => {
    if (!description) {
      setValidationStatus({ validateStatus: 'error', help: intl.formatMessage({ id: 'validationEmpty' }) });
      return false;
    }

    setValidationStatus({ validateStatus: null, help: '', hasFeedback: false });
    return true;
  };

  const onSubmit = () => {
    if (validate(damageDesc)) {
      onCreate(damageDesc);
      setDamageDesc('');
    }
  };

  return (
    <Popover
      {...rest}
      trigger="click"
      placement="right"
      title="Create a damage type"
      content={(
        <div style={{ display: 'flex' }}>
          <Form.Item
            hasFeedback={validationStatus.hasFeedback}
            validateStatus={validationStatus.validateStatus}
            help={validationStatus.help}
            style={{ marginBottom: '0', marginRight: '0.6em' }}
          >
            <Input
              ref={input => input && input.focus()} // Focus the input when rendered.
              type="text"
              value={damageDesc}
              onChange={(e) => {
                validate(e.target.value);
                setDamageDesc(e.target.value);
              }}
              onPressEnter={onSubmit}
              placeholder={intl.formatMessage({ id: 'enter' })}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: '0' }}>
            <Button type="ghost" onClick={onSubmit}>{intl.formatMessage({ id: 'create' })}</Button>
          </Form.Item>
        </div>
      )}
    >
      {children}
    </Popover>
  );
});

CreateDamagePopover.propTypes = {
  damageTypes: PropTypes.arrayOf(damageTypeShape).isRequired,
  onCreate: PropTypes.func.isRequired,
  children: PropTypes.any.isRequired, // eslint-disable-line react/forbid-prop-types
};


/**
 * The field for linking the damage types in the damage area.
 * This also has the ability to add new damage types.
 */
const DamageTypeSelect = injectIntl(({
  form, intl, area, damageTypes, damageTypesLoading,
}) => {
  const [isVisible, setVisible] = useState(false);

  const onAdd = (newDesc) => {
    setVisible(false);

    const fieldDamageTypes = form.getFieldValue(damageTypeFieldId);
    const existingDT = damageTypes.find(dt => newDesc.toLowerCase() === dt.damageTypeDescription.toLowerCase());
    const alreadyInserted = !!fieldDamageTypes.find(fieldDesc => fieldDesc.toLowerCase() === newDesc.toLowerCase());

    if (!alreadyInserted) {
      form.setFieldsValue({
        [damageTypeFieldId]: [
          ...fieldDamageTypes,
          existingDT ? existingDT.damageTypeDescription : newDesc,
        ],
      });
    }
  };

  return (
    <Form.Item required label={<FormattedMessage id="damageType" />}>
      <div style={{ display: 'flex' }}>
        { createDamageTypeField(form, area, damageTypes, intl, damageTypesLoading) }
        <CreateDamagePopover intl={intl} visible={isVisible} onVisibleChange={setVisible} onCreate={onAdd} damageTypes={damageTypes}>
          <Button style={{ marginLeft: '0.6em' }}>Add</Button>
        </CreateDamagePopover>
      </div>
    </Form.Item>
  );
});


/**
 * Takes the field data and separates the already saved damage types
 * from the newly created damage types.
 * @param {string[]} fieldDescriptions The descriptions in the select field.
 * @param {object[]} existingDamageTypes All available damage types.
 * @returns {object[]} An array.
 *  [0] has existing damage type objects.
 *  [1] has new damage type descriptions.
 */
const getExistingAndNewDTs = (fieldDescriptions, existingDamageTypes) => (
  fieldDescriptions.reduce((accumulator, desc) => {
    const existingDamageType = existingDamageTypes.find(dt => dt.damageTypeDescription === desc);
    if (existingDamageType) {
      accumulator[0].push(existingDamageType);
    } else {
      accumulator[1].push({ active: true, damageTypeDescription: desc });
    }
    return accumulator;
  }, [[], []])
);


const EditDamageArea = injectIntl(Form.create({ name: 'edit-damage-area' })(({
  intl, form, visible, area, onCreate, onUpdate, onCancel, damageTypesLoading, damageTypes,
  modelTypesLoading, modelTypes, isSubmitting, validationBox, descValidator, getDamageTypes, ...rest
}) => {
  const isCreating = !area;
  const buttonId = 'save';
  const isEditingDiagramArea = !!((area && area.isDiagramDamageArea));

  const titleId = () => {
    if (!isCreating) return isEditingDiagramArea ? 'editDiagramDamage' : 'updateDamageArea';

    return 'createDamageArea';
  };

  const { resetFields } = form;

  // Reset fields when it becomes visible.
  useEffect(() => {
    if (visible) resetFields();
    if (area && !area.linkedDamageTypeCodes) getDamageTypes(area);
  }, [resetFields, visible, area, getDamageTypes]);

  // On form submit.
  const submitDamageArea = useCallback(() => {
    form.validateFields((err, values) => {
      if (err) return; // Nothing more to do.

      const fieldDamageTypes = values.linkedDamageTypeCodes;

      // Separate the existing damage types and the new damage types
      const [existingDamageTypes, newDamageTypes] = getExistingAndNewDTs(fieldDamageTypes, damageTypes);

      const newdamageAreaLocationType = (data) => {
        if (!isEditingDiagramArea) return (data.damageAreaLocation === 'INTERIOR') ? 'INTERIOR USER-DEFINED' : 'EXTERIOR USER-DEFINED';

        return 'EXTERIOR DIAGRAM';
      };

      if (isCreating) {
        const newArea = {
          ...values,
          linkedDamageTypeCodes: existingDamageTypes.map(dt => dt.damageTypeCode),
          active: false,
          isDiagramDamageArea: false,
          damageAreaLocationType: newdamageAreaLocationType(values),
        };

        onCreate(newArea, newDamageTypes);
      } else {
        const updatedArea = {
          ...area,
          ...values,
          linkedDamageTypeCodes: existingDamageTypes.map(dt => dt.damageTypeCode),
          damageAreaLocationType: newdamageAreaLocationType(values),
        };

        onUpdate(updatedArea, newDamageTypes);
      }
    });
  }, [form, area, damageTypes, isCreating, onCreate, onUpdate, isEditingDiagramArea]);

  return (
    <Modal
      destroyOnClose
      title={<FormattedMessage id={titleId()} />}
      okText={<FormattedMessage id={buttonId} />}
      visible={visible}
      onOk={submitDamageArea}
      onCancel={onCancel}
      confirmLoading={isSubmitting}
      {...rest}
    >
      <Form layout="vertical" onSubmit={(e) => { e.preventDefault(); submitDamageArea(); }}>
        <ViaValidationAlert validationError={validationBox} />
        <DescriptionField form={form} area={area} checkForDuplicates={descValidator} />
        {
          !isEditingDiagramArea ? (
            <div>
              <LocationField form={form} area={area} />
              <ModelTypeField form={form} area={area} modelTypes={modelTypes} modelTypesLoading={modelTypesLoading} />
            </div>
          )
            : ''
        }
        <DamageTypeSelect form={form} area={area} damageTypes={damageTypes} damageTypesLoading={damageTypesLoading} />
      </Form>
    </Modal>
  );
}));

EditDamageArea.propTypes = {
  visible: PropTypes.bool.isRequired,
  area: damageAreaShape,
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  damageTypesLoading: PropTypes.bool,
  damageTypes: PropTypes.arrayOf(damageTypeShape).isRequired,
  modelTypesLoading: PropTypes.bool,
  modelTypes: PropTypes.arrayOf(modelTypeShape).isRequired,
  validationBox: PropTypes.shape({
    errorMessage: PropTypes.string,
    errors: PropTypes.arrayOf(PropTypes.string),
  }),
};

EditDamageArea.defaultProps = {
  area: null,
  damageTypesLoading: false,
  modelTypesLoading: false,
  validationBox: null,
};


const mapStateToProps = (state) => {
  const editState = selectEditState(state);
  const submitRequest = selectSubmitDamageAreaRequest(state);
  const damageTypesRequest = selectDamageTypesRequest(state);
  const damageTypes = selectDamageTypes(state);
  const modelTypesRequest = selectModelTypesRequest(state);
  const modelTypes = selectModelTypes(state);
  const damageTypesForAreaRequest = selectFetchDamageTypesForAreaRequest(state);
  const damageTypesAllLoading = damageTypesRequest ? damageTypesRequest.isLoading : false;
  const damageTypesForAreaLoading = damageTypesForAreaRequest ? damageTypesForAreaRequest.isLoading : false;

  return {
    visible: editState.isEditing,
    area: editState.damageAreaCode ? selectDamageArea(state, editState.damageAreaCode) : null,
    isSubmitting: submitRequest ? submitRequest.isLoading : false,
    damageTypesLoading: damageTypesAllLoading || damageTypesForAreaLoading,
    damageTypes,
    modelTypesLoading: modelTypesRequest ? modelTypesRequest.isLoading : false,
    modelTypes: modelTypes || [],
    validationBox: submitRequest ? submitRequest.error : undefined,
  };
};

const mapDispatchToProps = dispatch => ({
  getDamageTypes: area => dispatch(requestDamageTypesForArea(area)),
  onCreate: (area, newDamageTypes) => dispatch(requestAddDamageArea(area, newDamageTypes)),
  onUpdate: (area, newDamageTypes) => dispatch(requestUpdateDamageArea(area, newDamageTypes)),
  onCancel: () => dispatch(cancelEditDamageArea()),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditDamageArea);
